import { useCallback, useEffect, useState } from "react";
import { ApiError, api, type SavedDesign } from "./api";
import { errorKey, useI18n } from "./i18n";

export default function History({
  refreshKey,
  onOpen,
  onAuthError,
}: {
  refreshKey: number;
  onOpen: (d: SavedDesign) => void;
  onAuthError: (err: unknown) => boolean;
}) {
  const { lang, t } = useI18n();
  const [items, setItems] = useState<SavedDesign[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setItems(await api.listDesigns());
      setError(null);
    } catch (err) {
      if (!onAuthError(err)) {
        setError(t(err instanceof ApiError ? errorKey(err.code) : "error.history_load_failed"));
      }
    } finally {
      setLoading(false);
    }
  }, [onAuthError, t]);

  useEffect(() => {
    void load();
  }, [load, refreshKey]);

  const remove = useCallback(
    async (id: string) => {
      // Optimistic: drop it from the list and put it back if the delete fails.
      const previous = items;
      setItems((xs) => xs.filter((x) => x.id !== id));
      try {
        await api.deleteDesign(id);
      } catch (err) {
        setItems(previous);
        if (!onAuthError(err)) {
          setError(t(err instanceof ApiError ? errorKey(err.code) : "error.design_delete_failed"));
        }
      }
    },
    [items, onAuthError, t],
  );

  const open = useCallback(
    async (id: string) => {
      try {
        onOpen(await api.getDesign(id));
      } catch (err) {
        if (!onAuthError(err)) {
          setError(t(err instanceof ApiError ? errorKey(err.code) : "error.design_open_failed"));
        }
      }
    },
    [onOpen, onAuthError, t],
  );

  if (loading && items.length === 0) {
    return <p className="opacity-70">{t("history.loadingYours")}</p>;
  }

  return (
    <section>
      <h2 className="mb-3 text-xs font-semibold uppercase tracking-widest opacity-60">
        {t("history.yours")}
      </h2>

      {error && <p className="mb-3 text-[var(--color-bad)]">{error}</p>}

      {items.length === 0 ? (
        <p className="opacity-70">{t("history.emptyLong")}</p>
      ) : (
        <ul className="flex flex-col gap-2">
          {items.map((d) => (
            <li
              key={d.id}
              className="flex items-center justify-between gap-3 rounded border border-current/15 px-3 py-2"
            >
              <button
                type="button" onClick={() => void open(d.id)}
                className="min-w-0 flex-1 text-left"
              >
                <span className="block truncate font-medium">{d.name}</span>
                <span className="block text-sm tabular-nums opacity-60">
                  {d.mmWidth.toFixed(0)} × {d.mmHeight.toFixed(0)} mm ·{" "}
                  {t(d.mode === "cut" ? "params.mode.cut" : "params.mode.draw")} ·{" "}
                  {d.outline && (
                    <>
                      {t("history.outlineTag")}
                      {(d.outlineMarginMm ?? 0) > 0 && ` +${d.outlineMarginMm} mm`} ·{" "}
                    </>
                  )}
                  {new Date(d.createdAt).toLocaleDateString(lang)}
                </span>
              </button>
              <button
                type="button" onClick={() => void remove(d.id)}
                aria-label={t("history.deleteLabel", { name: d.name })}
                className="shrink-0 rounded px-2 py-1 text-sm opacity-60 hover:bg-current/10 hover:opacity-100"
              >
                {t("history.delete")}
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
